import { useState } from "react";
import { useLocale, useT } from "@shared/client/locale";
import { useRouter } from "@shared/client/navigation";
import { Archive, ChevronDown, Globe2, LoaderCircle, Pencil, Plus } from "lucide-react";
import { OfferFavicon } from "@/components/offer-favicon";
import { cadenceLabel, hostOf, type OfferWithProfile } from "@shared/lib/offers-shared";
import { cn } from "@shared/lib/utils";
import { StatusDot } from "@shared/components/blocks";
import { OfferProfileForm } from "@/components/settings/offer-profile-form";
import { hasTargeting, OfferProfileView } from "@/components/settings/offer-profile-view";
import { UpgradeLink } from "@/components/billing/plan-limit";
import { WatchControls } from "@/components/settings/watch-controls";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";

/**
 * « Mes offres » : l'écran où l'on décide ce que l'agent cherche.
 *
 * Une offre y est un objet complet — son adresse, sa veille, son ciblage — et
 * un compte peut en porter plusieurs : deux produits, deux marchés, deux
 * pipelines qui ne se mélangent pas. Ajouter une offre est donc une action
 * ordinaire, pas un contournement (retaper une autre URL dans le champ
 * d'analyse, comme avant, créait une offre fantôme que personne n'avait
 * choisie).
 */

/** Ajout d'une offre : une adresse suffit, l'agent en déduit le reste. */
function AddOffer({ plan, used }: { plan: OffersPlan; used: number }) {
  const t = useT().settings.offers.add;
  const router = useRouter();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [limited, setLimited] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setError("");
    setLimited(false);
    try {
      const response = await fetch("/api/offers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim() }),
      });
      const data = await response.json();
      if (!response.ok) {
        setLimited(response.status === 402);
        throw new Error(data.error || t.failed);
      }
      setUrl("");
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.failed);
    } finally {
      setBusy(false);
    }
  }

  if (used >= plan.maxOffers) {
    return (
      <Card id="ajouter">
        <CardHeader>
          <CardTitle className="text-[15px]">{t.title}</CardTitle>
          <CardDescription>{t.limitReached(plan.name, plan.maxOffers)}</CardDescription>
        </CardHeader>
        <CardContent className="text-[13px]">
          <UpgradeLink />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="ajouter">
      <CardHeader>
        <CardTitle className="text-[15px]">{t.title}</CardTitle>
        <CardDescription>{t.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-wrap items-end gap-4" onSubmit={submit}>
          <div className="grid min-w-[260px] flex-1 gap-2">
            <Label className="sr-only" htmlFor="new-offer-url">
              {t.urlLabel}
            </Label>
            <div className="relative">
              <Globe2
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                autoComplete="url"
                className="h-10 pl-10"
                disabled={busy}
                id="new-offer-url"
                inputMode="url"
                onChange={(event) => setUrl(event.target.value)}
                placeholder={t.urlPlaceholder}
                type="text"
                value={url}
              />
            </div>
          </div>
          <Button className="h-10 gap-2" disabled={busy || !url.trim()} type="submit">
            {busy ? <LoaderCircle className="animate-spin" size={16} /> : <Plus size={16} />}
            {t.submit}
          </Button>
        </form>
        {error && (
          <Alert className="mt-4" variant="destructive">
            <AlertDescription>
              {error} {limited && <UpgradeLink />}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

/** Archivage : l'offre sort du sélecteur, ses leads et ses passages restent. */
function ArchiveOffer({ offerId, name }: { offerId: string; name: string }) {
  const t = useT().settings.offers.archive;
  const router = useRouter();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function archive() {
    setBusy(true);
    try {
      await fetch(`/api/offers/${offerId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archived: true }),
      });
      router.refresh();
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  if (!confirming) {
    return (
      <Button
        className="text-muted-foreground"
        onClick={() => setConfirming(true)}
        size="sm"
        type="button"
        variant="ghost"
      >
        <Archive size={14} aria-hidden="true" /> {t.button}
      </Button>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground">
      {t.confirm(name)}
      <Button disabled={busy} onClick={archive} size="sm" type="button" variant="destructive">
        {busy ? <LoaderCircle className="animate-spin" size={14} /> : t.button}
      </Button>
      <Button onClick={() => setConfirming(false)} size="sm" type="button" variant="ghost">
        {t.cancel}
      </Button>
    </span>
  );
}

/** L'état de la veille d'une offre : un point et quelques mots. */
function WatchStatus({ offer }: { offer: OfferWithProfile }) {
  const locale = useLocale();
  const t = useT().settings.offers.watchStatus;
  return offer.radarActive ? (
    <StatusDot tone="active">{t.active(cadenceLabel(offer.frequencyDays, locale))}</StatusDot>
  ) : (
    <StatusDot>{t.paused}</StatusDot>
  );
}

function OfferCard({
  offer,
  current,
  minFrequencyDays,
  readOnly,
}: {
  offer: OfferWithProfile;
  current: boolean;
  minFrequencyDays: number;
  readOnly: boolean;
}) {
  const t = useT().settings.offers.card;
  const router = useRouter();
  const [switching, setSwitching] = useState(false);
  const [editing, setEditing] = useState(false);
  // Seule l'offre consultée s'ouvre : trois fiches dépliées ne se lisent plus.
  const [open, setOpen] = useState(current);
  const targeted = hasTargeting(offer.profile);

  async function consult() {
    setSwitching(true);
    try {
      await fetch("/api/offers/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ offerId: offer.id }),
      });
      router.refresh();
    } finally {
      setSwitching(false);
    }
  }

  return (
    <article className="rounded-lg border">
      <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 p-5 max-sm:p-4">
        <button
          aria-expanded={open}
          className="flex min-w-0 cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-left"
          onClick={() => setOpen(!open)}
          type="button"
        >
          <span className="grid size-9 flex-none place-items-center rounded-lg border">
            <OfferFavicon size={18} url={offer.url} />
          </span>
          <span className="grid min-w-0">
            <span className="truncate font-serif text-[21px] tracking-tight">{offer.name}</span>
            <span className="text-[12.5px] text-muted-foreground">{hostOf(offer.url)}</span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn("size-4 flex-none text-muted-foreground transition-transform", open && "rotate-180")}
          />
        </button>
        <div className="flex flex-wrap items-center gap-3">
          <WatchStatus offer={offer} />
          {current ? (
            <Badge variant="secondary">{t.displayed}</Badge>
          ) : (
            !readOnly && (
              <Button disabled={switching} onClick={consult} size="sm" type="button" variant="outline">
                {switching && <LoaderCircle className="animate-spin" />} {t.display}
              </Button>
            )
          )}
        </div>
      </header>
      {open && (
        <>
          <section className="border-t px-5 pt-4 pb-2 max-sm:px-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="m-0 text-[14px] font-semibold">{t.targeting}</h3>
              {!readOnly && !editing && (
                <Button onClick={() => setEditing(true)} size="sm" type="button" variant="ghost">
                  <Pencil /> {t.edit}
                </Button>
              )}
            </div>
            {editing ? (
              <div className="py-4">
                <OfferProfileForm
                  onCancel={() => setEditing(false)}
                  onSaved={() => {
                    setEditing(false);
                    router.refresh();
                  }}
                  profile={offer.profile}
                />
              </div>
            ) : targeted ? (
              <OfferProfileView profile={offer.profile} />
            ) : (
              <p className="m-0 py-4 text-[13px] text-muted-foreground">{t.targetingPending}</p>
            )}
          </section>
          <section className="grid gap-3 border-t p-5 max-sm:p-4">
            <h3 className="m-0 text-[14px] font-semibold">{t.watch}</h3>
            {readOnly ? (
              <WatchStatus offer={offer} />
            ) : (
              <WatchControls minFrequencyDays={minFrequencyDays} offer={offer} />
            )}
          </section>
          {!readOnly && (
            <footer className="flex justify-end border-t px-5 py-2 max-sm:px-4">
              <ArchiveOffer name={offer.name} offerId={offer.id} />
            </footer>
          )}
        </>
      )}
    </article>
  );
}

type OffersPlan = { name: string; maxOffers: number; minFrequencyDays: number };

export function OffersManager({ offers, activeOfferId, plan, readOnly = false }: {
  offers: OfferWithProfile[];
  activeOfferId: string | null;
  plan: OffersPlan;
  /** Espace client consulté par un admin : on lit, on ne modifie rien. */
  readOnly?: boolean;
}) {
  const t = useT().settings.offers;
  return (
    <div className="grid gap-4">
      {offers.length === 0 ? (
        <Card className="items-center gap-3 py-14 text-center">
          <p className="m-0 max-w-[440px] text-[13px] text-muted-foreground">
            {t.empty}
          </p>
        </Card>
      ) : (
        offers.map((offer) => (
          <OfferCard
            current={offer.id === activeOfferId}
            key={offer.id}
            minFrequencyDays={plan.minFrequencyDays}
            offer={offer}
            readOnly={readOnly}
          />
        ))
      )}
      {!readOnly && <AddOffer plan={plan} used={offers.filter((offer) => !offer.archivedAt).length} />}
    </div>
  );
}
